import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { TranslationContext } from "../contexts/TranslationContext";
import API_BASE_URL from "../apiConfig";

const WalletForm = () => {
    const { translations } = useContext(TranslationContext);
    const validationSchema = Yup.object({
        key: Yup.string()
            .email(translations.invalidEmail) 
            .max(320, translations.walletReqMax)
            .required(translations.walletReq),
    });
    const navigate = useNavigate();  
    const [isLoading, setIsLoading] = useState(false); // State for loading button
    const [key, setKey] = useState(""); // State to hold wallet key
    const [renewDate, setRenewDate] = useState(null);

    // Trigger SweetAlert2 Toast on Validation Error
    const handleValidationError = () => {
      if (formik.touched.key && formik.errors.key) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: formik.errors.key,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    };
    // Fetch the wallet key when the component mounts
    const fetchKey = async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            Swal.fire("Error", "Please log in to view your key.", "error");
            navigate("/se-connecter");
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/wallets`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data?.data?.key) {
                    setKey(data.data.key);
                } else {
                    setKey(null);
                }
            } else {
                Swal.fire("Error", "Failed to load wallet key.", "error");
            }
        } catch (error) {
            console.error("Error fetching wallet key:", error);
            Swal.fire("Error", "An unexpected error occurred. Please try again.", "error");
        }
    };

    useEffect(() => {
        fetchKey();
    }, [navigate]);

    const formik = useFormik({
        initialValues: {
            key: "",
        },
        validationSchema,
        onSubmit: async (values, {resetForm}) => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    Swal.fire("Error", "No token found. Please log in again.", "error");
                    navigate("/se-connecter");
                    return;
                }

                const response = await fetch(`${API_BASE_URL}/api/wallets`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        key: values.key,
                        type: "bitcoin",
                    }),
                });

                if (response.ok) {
                    Swal.fire({
                        title: translations.successTitle,
                        text: translations.walletUpdate,
                        icon: "success",
                        showConfirmButton: false,  
                        timer: 3000  
                    });                    
                    fetchKey();
                    resetForm();
                }else {
                    const errorData = await response.json();
                    Swal.fire({
                        icon: "error",
                        title: "Erreur",
                        text: errorData.message || "La mise à jour de l'adresse email a échoué.",
                        toast: true,
                        position: "top-end",  
                        showConfirmButton: false,  
                        timer: 2000,  
                        timerProgressBar: true,  
                    });
                }
                
            } catch (error) {
                console.error("Error sending key:", error);
                Swal.fire("Error", "An unexpected error occurred. Please try again.", "error");
            } finally {
                setIsLoading(false);
            }
        },
    });

    const [isSubscribed, setIsSubscribed] = useState(null);
    const [error, setError] = useState("");
    const [showPaymentForm, setShowPaymentForm] = useState(false);

    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setError("User is not authenticated. Please log in.");
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/api/user-subscriptions`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const result = await response.json();

                if (response.ok) {
                    if (result.success === true) {
                        setIsSubscribed(true);
                        setRenewDate(result.subscription.next_billing_date);
                    } else {
                        setIsSubscribed(false);
                    }
                } else {
                    setError(result.message);
                }
            } catch (error) {
                setError("An error occurred: " + error.message);
            }
        };

        fetchSubscriptionStatus();
    }, []);

    const handleSubscriptionSuccess = (renewDate) => {
        setIsSubscribed(true);
        setRenewDate(renewDate);
        setShowPaymentForm(false);

        // Optionally, re-fetch subscription status to ensure consistency
        const fetchSubscriptionStatus = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                setError("User is not authenticated. Please log in.");
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/api/user-subscriptions`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const result = await response.json();

                if (response.ok) {
                    if (result.success === true) {
                        setIsSubscribed(true);
                        setRenewDate(result.subscription.next_billing_date);
                    } else {
                        setIsSubscribed(false);
                    }
                } else {
                    setError(result.message);
                }
            } catch (error) {
                setError("An error occurred: " + error.message);
            }
        };

        fetchSubscriptionStatus();
    };

    return (
        <>
            <div className="wallte-details text-start mb-3 row">
                <div className="col-12 d-none">
                    <h6><strong>{translations.walletAddress}</strong> </h6>
                    <h6>{key || translations.noWalletAddress}</h6>
                </div>
                <div className="col-4 d-none">
                    <h6><strong>{translations.renewDate}</strong> </h6>
                    <h6>{renewDate}</h6>
                </div>
            </div>
            <form onSubmit={formik.handleSubmit}>
                <div className="mb-2">
                    <label htmlFor="wallet" className="form-col-label walletLabel mb-2">
                       {translations.walletLabel}
                    </label>
                    <input
                        type="email"
                        className={`form-control w-100 rounded-pill input-field ${formik.touched.key && formik.errors.key ? "is-invalid" : ""}`}
                        name="key"
                        value={formik.values.key}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                            formik.handleBlur(e);
                            handleValidationError();
                          }}
                    /> 
                </div>
                <div className="mb-3 col-12">
                    <button
                        type="submit"
                        className="btn btn-light bgprimary border-0 py-3 rounded-pill oavlite-text-primary fw-bold w-100 mb-3"
                        disabled={isLoading}
                    >
                        {isLoading
                            ? translations.saving
                            : key
                                ? translations.update
                                : translations.registerWallet}
                    </button>
                </div>
            </form>
        </>
    );
};

export default WalletForm;
